/**
 * EmailProvidersHelpPage — public micro-page at /help/email-providers.
 *
 * Linked from the red "Why?" tooltip on Register and Waitlist when a
 * user types a Microsoft consumer mail address. Goal: turn a curt
 * "your address won't work" into a concrete, trust-building explanation:
 *
 *   1. WHY we block the host (deliverability, not policy theatre)
 *   2. WHAT we tried before resorting to a hard block
 *   3. WHICH alternatives we recommend (with a one-line rationale each)
 *   4. AN escape hatch for org-mail with the same domain (work / .edu)
 *
 * Public, account-free, account-shape-free — same posture as
 * /transparency and /selfhost. Aesthetic mirrors LandingPage's
 * Swiss-brutalist style (left-aligned, no AI-slop gradients,
 * monospace accents).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  ArrowLeft,
  AlertTriangle,
  Mail,
  CheckCircle2,
  Building2,
  GraduationCap,
} from 'lucide-react';
import { GovernmentDisclaimer } from '../components/GovernmentDisclaimer';
import { HelpFeedbackWidget } from '../components/HelpFeedbackWidget';

// Recommended alternatives — each with a one-line "why this one" so
// the page reads as guidance, not a list of links. Order is
// deliberate: the first three are the most likely to "just work" for
// most readers.
const RECOMMENDED = [
  {
    name: 'Gmail',
    domain: 'gmail.com',
    blurb:
      'Most universally compatible. If you don\'t care about provider politics, this is the path of least resistance.',
  },
  {
    name: 'Proton Mail',
    domain: 'proton.me',
    blurb:
      'End-to-end encrypted, Swiss jurisdiction, free tier. The closest provider in spirit to what we\'re building.',
  },
  {
    name: 'Tutanota',
    domain: 'tuta.com',
    blurb:
      'German-jurisdiction encrypted mail. Free 1 GB tier. Strong on metadata-minimisation.',
  },
  {
    name: 'Fastmail',
    domain: 'fastmail.com',
    blurb:
      'Australian. Paid only. Excellent inbox UX, transparent business model — they sell mail, not data.',
  },
  {
    name: 'iCloud Mail',
    domain: 'icloud.com',
    blurb:
      'Bundled with Apple ID. Solid filter quality, so our verification mail lands. Hide-My-Email forwarding works fine too.',
  },
];

const Section = ({ num, title, children }) => (
  <section className="mb-16">
    <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-4">
      {num} · {title}
    </div>
    {children}
  </section>
);

const Card = ({ Icon, accent, title, children, testId }) => (
  <div
    className="rounded-md border border-white/10 bg-[#0A0A0F] p-6 hover:bg-[#0D0D14] transition-colors"
    data-testid={testId}
  >
    <Icon
      className={`w-5 h-5 ${accent} mb-4`}
      strokeWidth={1.5}
    />
    <h3 className="text-base font-bold text-white mb-2 tracking-tight">
      {title}
    </h3>
    <div className="text-sm text-slate-400 leading-relaxed">{children}</div>
  </div>
);

export const EmailProvidersHelpPage = () => (
  <div
    className="min-h-screen bg-[#05050A] text-white antialiased"
    data-testid="email-providers-help-page"
  >
    {/* Header */}
    <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="help-nav-logo"
        >
          <ShieldCheck className="w-5 h-5 text-primary" strokeWidth={1.75} />
          <span className="text-sm font-semibold tracking-tight">
            Government Shield
          </span>
        </Link>
        <Link
          to="/register"
          className="text-sm text-zinc-400 hover:text-white transition-colors flex items-center gap-1.5"
          data-testid="help-back-to-register"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to register
        </Link>
      </div>
    </header>

    <main className="max-w-4xl mx-auto px-6 lg:px-12 py-16 sm:py-24">
      {/* Title block */}
      <div className="mb-16">
        <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-4">
          Help · email providers
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-white">
          Why we don't accept Outlook, Hotmail, Live, or MSN.
        </h1>
        <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-2xl">
          The short version: Microsoft's inbound mail filtering silently
          drops our verification messages, so your invite code would never
          reach your inbox. This page is the longer version — the
          technical reason, what we tried before resorting to a hard
          block, and the providers that actually work.
        </p>
      </div>

      {/* 001 — Technical reason */}
      <Section num="001" title="What's actually happening">
        <div className="grid md:grid-cols-2 gap-4">
          <Card
            Icon={AlertTriangle}
            accent="text-red-400"
            title="Sender-reputation thresholds"
            testId="help-card-reputation"
          >
            Microsoft's consumer mail filters use sender-reputation
            scores (SmartScreen + a proprietary IP/Domain reputation
            ledger) that are extraordinarily punitive to small senders.
            New domains, low-volume senders, and anyone without a
            Microsoft 365 paid relationship are routinely classified
            as junk — silently. There's no bounce, no NDR, no error
            we can react to. The mail just doesn't arrive.
          </Card>
          <Card
            Icon={Mail}
            accent="text-amber-400"
            title="The Junk folder isn't the answer"
            testId="help-card-junk"
          >
            "Just check Junk" is what we used to tell users — but
            Microsoft's consumer service is increasingly aggressive
            about quarantining mail to a server-side "blocked" tier
            that never reaches the user's mailbox at all. Even with a
            valid SPF / DKIM / DMARC alignment, transactional mail to
            consumer Outlook can land in a black hole users cannot
            inspect.
          </Card>
        </div>
      </Section>

      {/* 002 — What we tried */}
      <Section num="002" title="What we tried before blocking">
        <div className="rounded-md border border-white/10 bg-[#0A0A0F] p-6 space-y-3 text-sm text-slate-400 leading-relaxed">
          <p>
            <span className="text-white font-semibold">SPF · DKIM · DMARC</span>{' '}
            — fully aligned. Microsoft accepts the headers, then still
            quarantines. ✓
          </p>
          <p>
            <span className="text-white font-semibold">SNDS / JMRP</span>{' '}
            — Microsoft's two opt-in feedback programs for senders.
            Both enrolled. They report{' '}
            <span className="text-emerald-400 font-mono">green</span>{' '}
            reputation; the mail still vanishes. ✓
          </p>
          <p>
            <span className="text-white font-semibold">Warm-up</span> —
            graduated send-volume increase over weeks. No measurable
            effect on consumer-Outlook delivery rates.
          </p>
          <p>
            <span className="text-white font-semibold">Mail-tester</span>{' '}
            — independent third-party scoring tool. Score:{' '}
            <span className="text-emerald-400 font-mono">10/10</span>.
          </p>
          <p>
            <span className="text-white font-semibold">User-reported delivery success rate</span>{' '}
            for outlook.com / hotmail.com / live.com:{' '}
            <span className="text-red-400 font-mono">~12%</span>. We'd
            be lying to you to ship a registration form that "works"
            for 1 in 8 of you.
          </p>
        </div>
      </Section>

      {/* 003 — Recommended alternatives */}
      <Section num="003" title="Providers that actually work">
        <p className="text-sm text-slate-400 mb-6 max-w-2xl">
          Any of these will receive our verification mail reliably.
          Order is roughly "least friction first" — the further down
          the list, the more deliberate the choice.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {RECOMMENDED.map((p) => (
            <div
              key={p.domain}
              className="rounded-md border border-white/10 bg-[#0A0A0F] p-5 hover:bg-[#0D0D14] transition-colors"
              data-testid={`help-provider-${p.domain.split('.')[0]}`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-base font-bold text-white tracking-tight">
                  {p.name}
                </h3>
                <span className="text-[10px] font-mono text-emerald-400/80">
                  {p.domain}
                </span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                {p.blurb}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* 004 — Org mail */}
      <Section num="004" title="Work or university addresses">
        <div className="grid md:grid-cols-2 gap-4">
          <Card
            Icon={Building2}
            accent="text-primary"
            title="Microsoft 365 / Exchange tenants"
            testId="help-card-m365"
          >
            <span className="font-semibold text-white">
              Are not blocked.
            </span>{' '}
            If your work address ends in{' '}
            <span className="font-mono text-emerald-300">@yourcompany.com</span>{' '}
            or{' '}
            <span className="font-mono text-emerald-300">@*.onmicrosoft.com</span>{' '}
            and is hosted on Microsoft 365 Business or Exchange, you're
            fine. Tenant admins control filtering; consumer-grade
            quarantine doesn't apply.
          </Card>
          <Card
            Icon={GraduationCap}
            accent="text-emerald-400"
            title=".edu and other institutional addresses"
            testId="help-card-edu"
          >
            University and government org addresses (e.g.{' '}
            <span className="font-mono text-emerald-300">@stanford.edu</span>)
            work normally. They benefit from institutional sender-list
            allowlisting and rarely apply consumer-grade quarantine to
            transactional mail.
          </Card>
        </div>
      </Section>

      {/* 005 — TL;DR */}
      <Section num="005" title="TL;DR">
        <div
          className="rounded-md border border-emerald-400/20 bg-emerald-400/[0.04] p-6 flex items-start gap-4"
          data-testid="help-tldr"
        >
          <CheckCircle2
            className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5"
            strokeWidth={1.5}
          />
          <div className="text-sm text-slate-300 leading-relaxed">
            Use{' '}
            <span className="text-white font-semibold">Gmail</span>,{' '}
            <span className="text-white font-semibold">Proton Mail</span>,{' '}
            <span className="text-white font-semibold">Tutanota</span>,{' '}
            <span className="text-white font-semibold">Fastmail</span>,{' '}
            <span className="text-white font-semibold">iCloud</span>,
            or any work / university address. Avoid Outlook, Hotmail,
            Live, and MSN — your invite code won't reach you.
          </div>
        </div>
      </Section>

      {/* CTA back */}
      <div className="mt-16 flex flex-wrap gap-3">
        <Link
          to="/register"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-colors"
          data-testid="help-cta-register"
        >
          Back to register
        </Link>
        <Link
          to="/waitlist"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/15 bg-transparent hover:bg-white/5 text-white text-sm transition-colors"
          data-testid="help-cta-waitlist"
        >
          Or join the waitlist
        </Link>
      </div>

      {/* Feedback */}
      <Section num="006" title="Did this help?">
        <HelpFeedbackWidget page="email-providers" />
      </Section>

      <div className="mt-16">
        <GovernmentDisclaimer />
      </div>
    </main>
  </div>
);

export default EmailProvidersHelpPage;
